import { AlertTriangle, CalendarClock, StickyNote } from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseOrderNotes } from '@/lib/order-notes';

interface OrderNotesPanelProps {
  notes: string | null | undefined;
  /** Delivery date from the order record; the parsed note date wins if present. */
  deliveryDate?: string | null;
  className?: string;
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Renders order notes as structured facts rather than a prose blob.
 *
 * Delivery constraints are promoted to the top because they are needed at
 * packing time — see docs/fulfilment-ux-review.md §3.6. Until now this text
 * existed only on the full order page, which the packer never opens.
 */
export function OrderNotesPanel({ notes, deliveryDate, className }: OrderNotesPanelProps) {
  const parsed = parseOrderNotes(notes);
  const required = formatDate(parsed.requiredDeliveryDate ?? deliveryDate ?? null);

  const hasAnything =
    parsed.constraints.length > 0 || parsed.rest.length > 0 || required || parsed.channel;
  if (!hasAnything) return null;

  return (
    <div className={cn('space-y-2', className)}>
      {parsed.constraints.length > 0 && (
        <div className="rounded-md border border-warning/25 bg-warning/5 p-3">
          <div className="mb-1.5 flex items-center gap-1.5">
            <AlertTriangle size={13} className="shrink-0 text-warning" />
            <span className="text-xs font-semibold uppercase tracking-wider text-warning">
              Delivery constraints
            </span>
          </div>
          <ul className="space-y-1">
            {parsed.constraints.map((c) => (
              <li key={c} className="text-sm leading-relaxed text-foreground">
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(required || parsed.channel) && (
        <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-md border border-border bg-muted/40 px-3 py-2.5">
          {required && (
            <div className="min-w-0">
              <span className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <CalendarClock size={11} />
                Required by
              </span>
              <span className="mt-0.5 block text-sm font-medium tabular-nums text-foreground">
                {required}
              </span>
            </div>
          )}
          {parsed.channel && (
            <div className="min-w-0">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Placed via
              </span>
              <span className="mt-0.5 block truncate text-sm font-medium text-foreground">
                {parsed.channel}
              </span>
            </div>
          )}
        </div>
      )}

      {parsed.rest.length > 0 && (
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-1.5 flex items-center gap-1.5">
            <StickyNote size={13} className="shrink-0 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Notes
            </span>
          </div>
          <ul className="space-y-1">
            {parsed.rest.map((line) => (
              <li key={line} className="text-sm leading-relaxed text-muted-foreground">
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default OrderNotesPanel;
